/**
 * Copias internas del teléfono (design.md, 10.3; Req. 2.10, 6.10, 6.11).
 *
 * Viven en una base de IndexedDB SEPARADA de la principal a propósito:
 * - restaurar borra todas las tablas de la base principal, y la copia tiene
 *   que sobrevivir para poder deshacer;
 * - los respaldos recorren solo las tablas de la base principal, así que las
 *   copias nunca entran a un archivo exportado;
 * - si la base principal no abre tras una actualización, esta sí, y desde la
 *   pantalla de recuperación se puede descargar la copia.
 *
 * Se guarda la última copia de cada motivo: la nueva reemplaza a la anterior.
 * - antes_de_restaurar: los datos que había antes de restaurar; "Deshacer".
 * - antes_de_deshacer: los que había antes de deshacer, por si se registró
 *   algo después de restaurar. Solo se descarga; no se encadena otro deshacer.
 * - antes_de_actualizar: los de antes de que una versión nueva migre la base.
 * No se cifran, igual que el resto de los datos del teléfono (Req. 6.23).
 */

import Dexie, { type Table } from 'dexie';
import {
  analizarRespaldo,
  nombrePerfil,
  registrosCapturados,
  selloLocal,
  serializarRespaldo,
  type ArchivoRespaldo,
} from './backup-format';
import { ahoraIso } from '@/domain/dates';
import { ErrorRespaldo } from '@/domain/errors';
import type { NombreTabla } from './types';

export const NOMBRE_BASE_COPIAS = 'app-gym-salud-copias';

export type MotivoCopia = 'antes_de_restaurar' | 'antes_de_deshacer' | 'antes_de_actualizar';

export const ETIQUETAS_MOTIVO: Readonly<Record<MotivoCopia, string>> = {
  antes_de_restaurar: 'Antes de la última restauración',
  antes_de_deshacer: 'Antes de deshacer la última restauración',
  antes_de_actualizar: 'Antes de la última actualización de datos',
};

export interface CopiaInterna {
  /** Igual al motivo: una sola copia por motivo. */
  id: MotivoCopia;
  motivo: MotivoCopia;
  creadaEn: string;
  /** Resumen para mostrarla sin analizar el contenido. */
  perfil: string;
  totalRegistros: number;
  schemaVersion: number;
  /** Un ArchivoRespaldo (formato 1) serializado, igual a un respaldo exportado. */
  contenido: string;
}

export class BaseCopias extends Dexie {
  copias!: Table<CopiaInterna, MotivoCopia>;

  constructor(nombre = NOMBRE_BASE_COPIAS) {
    super(nombre);
    this.version(1).stores({ copias: 'id, creadaEn' });
  }
}

export const baseCopias = new BaseCopias();

/**
 * Guarda la copia y la VERIFICA: la vuelve a leer de la base y comprueba la
 * suma y los conteos. Si la verificación falla lanza, y quien llama no debe
 * continuar con la restauración o la actualización.
 */
export async function guardarCopia(
  motivo: MotivoCopia,
  archivo: ArchivoRespaldo,
  base: BaseCopias = baseCopias,
): Promise<CopiaInterna> {
  const copia: CopiaInterna = {
    id: motivo,
    motivo,
    creadaEn: ahoraIso(),
    perfil: nombrePerfil(archivo.perfil),
    totalRegistros: registrosCapturados(archivo.data as unknown as Record<NombreTabla, readonly unknown[]>),
    schemaVersion: archivo.schemaVersion,
    contenido: serializarRespaldo(archivo),
  };

  await base.copias.put(copia);

  const guardada = await base.copias.get(motivo);
  if (guardada === undefined) {
    throw new ErrorRespaldo('La copia interna no quedó guardada.');
  }
  const { resumen } = await analizarRespaldo(guardada.contenido);
  const completa = resumen.conteos.every(
    (c) => c.registros === (archivo.data[c.tabla]?.length ?? 0),
  );
  if (!resumen.checksumValido || !completa) {
    throw new ErrorRespaldo('La copia interna no pasó la verificación.');
  }
  return guardada;
}

/** Lectura pura, apta para useLiveQuery. */
export async function leerCopia(
  motivo: MotivoCopia,
  base: BaseCopias = baseCopias,
): Promise<CopiaInterna | undefined> {
  return base.copias.get(motivo);
}

export type ResumenCopia = Omit<CopiaInterna, 'contenido'> & { bytes: number };

/** Las copias existentes, más reciente primero. Lectura pura, apta para useLiveQuery. */
export async function listarCopias(base: BaseCopias = baseCopias): Promise<ResumenCopia[]> {
  const copias = await base.copias.orderBy('creadaEn').reverse().toArray();
  return copias.map(({ contenido, ...resto }) => ({ ...resto, bytes: contenido.length }));
}

/** `"duofit-copia-antes-de-restaurar-2026-10-21-1530.respaldo.json"` */
export function nombreArchivoCopia(copia: Pick<CopiaInterna, 'motivo' | 'creadaEn'>): string {
  const fecha = new Date(copia.creadaEn);
  const sello = Number.isNaN(fecha.getTime()) ? 'sin-fecha' : selloLocal(fecha);
  return `duofit-copia-${copia.motivo.replace(/_/g, '-')}-${sello}.respaldo.json`;
}
